import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus, Search, CheckCircle2, XCircle, Plus, Minus } from 'lucide-react';

import {
  Modal,
  Button,
  Input,
  Loading,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
  Card,
  CardContent,
} from '../../components/ui';
import { useRoles } from '../../shared/lib/hooks/use-roles';
import { useAssignUserRole, useRemoveUserRole } from '../../shared/lib/hooks/use-users';
import { isApiError } from '../../shared/api';
import type { User, Role } from '../../shared/types';

// Type for role with assignment status and change tracking
type RoleWithStatus = Role & {
  isOriginallyAssigned: boolean;
  isSelected: boolean;
};

interface AssignRoleDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AssignRoleDialog: React.FC<AssignRoleDialogProps> = ({
  user,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [selectedService, setSelectedService] = React.useState<string>('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Fetch roles data
  const { data: rolesData, isLoading: rolesLoading } = useRoles({
    search: searchTerm,
    serviceName: selectedService || undefined,
  });

  // Mutations
  const assignRoleMutation = useAssignUserRole();
  const removeRoleMutation = useRemoveUserRole();

  // Track role selections with their original assignment status
  const [rolesWithStatus, setRolesWithStatus] = React.useState<RoleWithStatus[]>([]);

  // Initialize roles when data loads or user changes
  React.useEffect(() => {
    if (!rolesData?.items || !user) {
      setRolesWithStatus([]);
      return;
    }

    const userRoleKeys = new Set(
      user.roles.map((role: Role) => `${role.id}-${role.serviceName || 'global'}`)
    );

    const roles = rolesData.items.map((role: Role): RoleWithStatus => {
      const roleKey = `${role.id}-${role.serviceName || 'global'}`;
      const isOriginallyAssigned = userRoleKeys.has(roleKey);
      return {
        ...role,
        isOriginallyAssigned,
        isSelected: isOriginallyAssigned,
      };
    });

    setRolesWithStatus(roles);
    setError(null);
  }, [rolesData?.items, user, open]);

  // Reset search when dialog closes
  React.useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSelectedService('');
      setError(null);
    }
  }, [open]);

  // Get unique service names for filtering
  const serviceNames = React.useMemo(() => {
    if (!rolesData?.items) return [];

    const services = new Set<string>();
    rolesData.items.forEach((role: Role) => {
      if (role.serviceName) {
        services.add(role.serviceName);
      }
    });

    return Array.from(services).sort();
  }, [rolesData?.items]);

  // Handle role toggle
  const handleRoleToggle = (roleId: string, serviceName: string | undefined) => {
    setRolesWithStatus((prev) =>
      prev.map((role) =>
        role.id === roleId && role.serviceName === serviceName
          ? { ...role, isSelected: !role.isSelected }
          : role
      )
    );
  };

  // Calculate changes
  const changes = React.useMemo(() => {
    const toAssign: RoleWithStatus[] = [];
    const toRemove: RoleWithStatus[] = [];

    rolesWithStatus.forEach((role) => {
      if (role.isSelected && !role.isOriginallyAssigned) {
        toAssign.push(role);
      } else if (!role.isSelected && role.isOriginallyAssigned) {
        toRemove.push(role);
      }
    });

    return { toAssign, toRemove, hasChanges: toAssign.length > 0 || toRemove.length > 0 };
  }, [rolesWithStatus]);

  // Handle form submission
  const handleSubmit = async () => {
    if (!user || !changes.hasChanges) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Assign new roles
      for (const role of changes.toAssign) {
        await assignRoleMutation.mutateAsync({
          userId: user.id,
          roleId: role.id,
          serviceName: role.serviceName,
        });
      }

      // Remove unselected roles
      for (const role of changes.toRemove) {
        await removeRoleMutation.mutateAsync({
          userId: user.id,
          roleId: role.id,
          serviceName: role.serviceName,
        });
      }

      onOpenChange(false);
    } catch (err) {
      console.error('Role management error:', err);
      if (isApiError(err)) {
        setError(err.message);
      } else {
        setError(t('errors.unknown', 'An unknown error occurred'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get role display status
  const getRoleStatus = (role: RoleWithStatus) => {
    if (role.isOriginallyAssigned && role.isSelected) {
      return 'kept'; // Was assigned, still assigned
    }
    if (role.isOriginallyAssigned && !role.isSelected) {
      return 'removing'; // Was assigned, will be removed
    }
    if (!role.isOriginallyAssigned && role.isSelected) {
      return 'adding'; // Was not assigned, will be added
    }
    return 'none'; // Was not assigned, still not assigned
  };

  const isLoading = isSubmitting || assignRoleMutation.isPending || removeRoleMutation.isPending;

  if (!user) {
    return null;
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('users.manageRolesForUser', `Manage Roles for ${user.email}`)}
      size="lg"
      className="max-w-2xl w-full mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto"
    >
      <div className="space-y-6">
        {/* Search and filter controls */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">
                {t('roles.searchRoles', 'Search roles')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('roles.searchPlaceholder', 'Search by role name...')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  disabled={isLoading}
                />
              </div>
            </div>

            {serviceNames.length > 0 && (
              <div className="sm:w-48">
                <label className="text-sm font-medium mb-2 block">
                  {t('roles.filterByService', 'Filter by service')}
                </label>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger disabled={isLoading}>
                    <SelectValue placeholder={t('roles.allServices', 'All services')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">
                      {t('roles.allServices', 'All services')}
                    </SelectItem>
                    {serviceNames.map((service) => (
                      <SelectItem key={service} value={service}>
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        {/* Changes summary */}
        {changes.hasChanges && (
          <div className="rounded-md bg-blue-50 p-3 space-y-2">
            <p className="text-sm font-medium text-blue-900">
              {t('roles.pendingChanges', 'Pending Changes')}:
            </p>
            <div className="flex flex-wrap gap-2">
              {changes.toAssign.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                  <Plus className="h-3 w-3 mr-1" />
                  {changes.toAssign.length} {t('roles.toAdd', 'to add')}
                </span>
              )}
              {changes.toRemove.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                  <Minus className="h-3 w-3 mr-1" />
                  {changes.toRemove.length} {t('roles.toRemove', 'to remove')}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Role selection */}
        <div>
          <label className="text-sm font-medium mb-2 block">
            {t('users.selectRoles', 'Select Roles')}
          </label>
          <div className="space-y-2">
            {rolesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loading size="sm" text={t('common.loading')} />
              </div>
            ) : rolesWithStatus.length === 0 ? (
              <div className="text-center py-8">
                <UserPlus className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {searchTerm || selectedService
                    ? t('roles.noRolesFoundFiltered', 'No roles found matching your criteria')
                    : t('roles.noRolesAvailable', 'No roles available')
                  }
                </p>
              </div>
            ) : (
              <div className="max-h-60 overflow-y-auto space-y-2 border rounded-md p-2">
                {rolesWithStatus.map((role) => {
                  const status = getRoleStatus(role);
                  const isAdding = status === 'adding';
                  const isRemoving = status === 'removing';
                  const isKept = status === 'kept';

                  return (
                    <Card
                      key={`${role.id}-${role.serviceName || 'global'}`}
                      className={`transition-colors cursor-pointer hover:bg-accent ${
                        isAdding ? 'ring-2 ring-green-500 bg-green-50' : ''
                      } ${
                        isRemoving ? 'ring-2 ring-red-500 bg-red-50 opacity-60' : ''
                      } ${
                        isKept ? 'bg-blue-50/50' : ''
                      }`}
                      onClick={() => handleRoleToggle(role.id, role.serviceName)}
                    >
                      <CardContent className="p-3">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={role.isSelected}
                            onChange={() => handleRoleToggle(role.id, role.serviceName)}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm">
                                {role.name}
                              </span>
                              {isKept && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  {t('roles.assigned', 'Assigned')}
                                </span>
                              )}
                              {isAdding && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">
                                  <Plus className="h-3 w-3 mr-1" />
                                  {t('roles.willAdd', 'Will add')}
                                </span>
                              )}
                              {isRemoving && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-700">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  {t('roles.willRemove', 'Will remove')}
                                </span>
                              )}
                              {role.serviceName && !isKept && !isAdding && !isRemoving && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-primary/10 text-primary">
                                  {role.serviceName}
                                </span>
                              )}
                            </div>
                            {role.description && (
                              <p className="text-xs text-muted-foreground mb-1">
                                {role.description}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              {role.permissions.length} {t('roles.permissions', 'permissions')}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Form-level Error Display */}
        {error && (
          <div className="rounded-md bg-destructive/15 p-3">
            <p className="text-sm text-destructive">
              {error}
            </p>
          </div>
        )}

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading || !changes.hasChanges}
            className="w-full sm:w-auto"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <Loading size="sm" className="mr-2" text="" />
                {t('common.saving')}
              </div>
            ) : (
              <>
                <UserPlus className="mr-2 h-4 w-4" />
                {t('common.saveChanges')}
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
